import { STATUS_LABELS } from '../orders/status';
import type { ConnectionState, Order } from '../types';

import { clear, h } from './dom';

export interface RenderState {
  connection: ConnectionState;
  storeName: string;
  left: Order[];
  right: Order[];
  loading: boolean;
}

export interface ErrorState {
  title: string;
  body: string;
  detail?: string | undefined;
}

/**
 * Maps a status to the CSS modifier class used on the card and pill.
 * Keeping this in one place makes it easy to recolour the screen.
 */
function statusModifier(status: Order['status']): string {
  switch (status) {
    case 'pending':
      return 'queued';
    case 'received':
      return 'received';
    case 'in_progress':
      return 'preparing';
    case 'waiting_for_pickup':
      return 'ready';
    case 'completed':
      return 'collected';
  }
}

function renderHeader(state: RenderState): HTMLElement {
  return h('header', {
    class: 'header',
    children: [
      h('div', {
        class: 'header__brand',
        children: [
          h('img', {
            class: 'header__logo',
            attrs: { src: '/logo.svg', alt: 'Joe & The Juice' },
          }),
          state.storeName
            ? h('h1', { class: 'header__store', text: state.storeName })
            : null,
        ],
      }),
      renderBadge(state.connection),
    ],
  });
}

function renderBadge(state: ConnectionState): HTMLElement {
  const isOnline = state === 'online';
  return h('div', {
    class: isOnline ? 'badge' : 'badge badge--offline',
    attrs: { 'data-testid': 'connection-badge', 'data-state': state },
    children: [
      h('span', { class: 'badge__dot' }),
      h('span', { class: 'badge__label', text: isOnline ? 'Online' : 'Offline' }),
    ],
  });
}

function renderCard(order: Order, lane: 'left' | 'right'): HTMLElement {
  const modifier = statusModifier(order.status);
  return h('article', {
    class: `card card--${modifier}`,
    attrs: { 'data-testid': 'order-card', 'data-order-id': order.id, title: order.id },
    children: [
      h('h2', { class: 'card__name', text: order.title }),
      lane === 'right'
        ? h('span', {
            class: `pill pill--${modifier}`,
            attrs: { 'data-testid': 'status-pill' },
            text: STATUS_LABELS[order.status],
          })
        : null,
    ],
  });
}

function renderLoadingBody(): HTMLElement {
  return h('div', {
    class: 'lane__loading',
    children: [
      h('div', { class: 'lane__loading-card' }),
      h('div', { class: 'lane__loading-card' }),
      h('div', { class: 'lane__loading-card' }),
    ],
  });
}

function renderLane(
  title: string,
  emptyTitle: string,
  emptyBody: string,
  orders: Order[],
  lane: 'left' | 'right',
  loading: boolean,
  scroll: boolean,
): HTMLElement {
  let body: HTMLElement;
  if (loading && orders.length === 0) {
    body = renderLoadingBody();
  } else if (orders.length === 0) {
    body = h('div', {
      class: 'lane__empty',
      attrs: { 'data-testid': `lane-empty-${lane}` },
      children: [
        h('p', { class: 'lane__empty-title', text: emptyTitle }),
        h('p', { class: 'lane__empty-body', text: emptyBody }),
      ],
    });
  } else {
    body = h('div', {
      class: 'lane__body',
      children: orders.map((o) => renderCard(o, lane)),
    });
  }

  return h('section', {
    class: scroll ? `lane lane--${lane} lane--scroll` : `lane lane--${lane}`,
    attrs: { 'data-testid': `lane-${lane}` },
    children: [
      h('div', {
        class: 'lane__head',
        children: [
          h('h2', { class: 'lane__title', text: title }),
        ],
      }),
      body,
    ],
  });
}

/**
 * Mounts the running app into `root` and returns a setState function. We
 * rebuild the DOM tree on every state push - on a portrait TV the screen
 * has <50 elements at any time so the cost is irrelevant compared to the
 * code complexity a diffing layer would buy.
 */
export interface RendererOptions {
  /** When true, lanes scroll (overflow-y: auto) instead of clipping overflow. */
  scroll?: boolean;
}

export function createRenderer(
  root: HTMLElement,
  options: RendererOptions = {},
): (state: RenderState) => void {
  const scroll = options.scroll ?? false;
  return (state) => {
    root.dataset.state = 'ready';
    clear(root);
    root.appendChild(renderHeader(state));
    root.appendChild(
      h('main', {
        class: 'lanes',
        children: [
          renderLane(
            'Preparing',
            'Waiting for the next order',
            'New orders will appear here as the team starts on them.',
            state.left,
            'left',
            state.loading,
            scroll,
          ),
          renderLane(
            'Ready for Pickup',
            'Nothing ready just yet',
            'Completed orders will show up here.',
            state.right,
            'right',
            state.loading,
            scroll,
          ),
        ],
      }),
    );
  };
}

export function renderErrorPanel(root: HTMLElement, err: ErrorState, showDetail: boolean): void {
  root.dataset.state = 'error';
  clear(root);
  root.appendChild(
    h('div', {
      class: 'panel',
      attrs: { 'data-testid': 'error-panel' },
      children: [
        h('div', {
          class: 'panel__inner',
          children: [
            h('h1', { class: 'panel__title', text: err.title }),
            h('p', { class: 'panel__body', text: err.body }),
            showDetail && err.detail
              ? h('pre', { class: 'panel__detail', text: err.detail })
              : null,
          ],
        }),
      ],
    }),
  );
}

export function renderLoading(root: HTMLElement, storeName: string): void {
  root.dataset.state = 'loading';
  clear(root);
  root.appendChild(
    h('div', {
      class: 'panel',
      attrs: { 'data-testid': 'loading-panel' },
      children: [
        h('div', {
          class: 'panel__inner',
          children: [
            h('h1', { class: 'panel__title', text: 'Loading orders…' }),
            h('p', {
              class: 'panel__body',
              text: storeName ? `Connecting to ${storeName}.` : 'Connecting…',
            }),
          ],
        }),
      ],
    }),
  );
}
